<script>
import { DateTime } from 'luxon';
import Layout from '@layouts/main';
import NewsRecordModel from '@models/NewsRecord';
import ReportModel from '@models/Report';
import HeaderComponent from '@components/HeaderComponent';
import FileDownloadLink from '@components/FileDownloadLink';

export default {
  name: 'HomeIndexPage',
  page() {
    return {
      title: this.$t('titles.home'),
    };
  },
  components: {
    Layout,
    HeaderComponent,
    FileDownloadLink,
  },
  data() {
    return {
      calendarEntries: [],
      calendarLoading: false,
      reports: [],
      reportsLoading: false,
      newsRecords: [],
      newsLoading: false,
      calendarFields: [
        { key: 'start', label: this.$t('table_labels.start_date_and_time'), formatter: this.formatCalendarDate },
        { key: 'end', label: this.$t('table_labels.end_date_and_time'), formatter: this.formatCalendarDate },
        { key: 'vip', label: this.$t('table_labels.vip') },
        { key: 'location', label: this.$t('table_labels.city_and_location') },
        { key: 'car_type', label: this.$t('table_labels.car_type') },
      ],
      reportFields: [
        { key: 'id', label: this.$t('table_labels.id') },
        { key: 'meeting_date', label: this.$t('table_labels.start_date'), formatter: this.formatSqlDate },
        { key: 'vip', label: this.$t('table_labels.vip') },
        { key: 'meeting_point', label: this.$t('table_labels.city_and_location') },
        { key: 'completed', label: this.$t('table_labels.status') },
      ],
      newsFields: [
        { key: 'created_at', label: this.$t('table_labels.date'), formatter: this.formatSqlDate },
        { key: 'title', label: this.$t('table_labels.topic') },
        { key: 'attachment', label: this.$t('table_labels.attachment') },
      ],
    };
  },
  created() {
    this.fetchCalendarEntries();
    this.fetchReports();
    this.fetchNews();
  },
  methods: {
    fetchCalendarEntries() {
      const now = DateTime.local();
      this.calendarLoading = true;

      return this.$http
        .get('calendar', {
          params: {
            start: now.toFormat('yyyy-MM-dd'),
            end: now.plus({ years: 1}).toFormat('yyyy-MM-dd'),
            hide_leaves: 1,
          },
        })
        .then((response) => {
          const entries = Array.isArray(response.data) ? response.data : response.data.data || [];
          this.calendarEntries = entries
            .filter((entry) => DateTime.fromISO(entry.start) >= now)
            .sort((left, right) => DateTime.fromISO(left.start).toMillis() - DateTime.fromISO(right.start).toMillis())
            .slice(0, 3);
        })
        .catch(() => {
          this.calendarEntries = [];
        })
        .finally(() => {
          this.calendarLoading = false;
        });
    },
    fetchReports() {
      this.reportsLoading = true;

      return ReportModel.orderBy('-id')
        .include('vip')
        .limit(3)
        .get()
        .then((response) => {
          this.reports = response.data || [];
        })
        .catch(() => {
          this.reports = [];
        })
        .finally(() => {
          this.reportsLoading = false;
        });
    },
    fetchNews() {
      this.newsLoading = true;

      return NewsRecordModel.orderBy('-id')
        .limit(3)
        .get()
        .then((response) => {
          this.newsRecords = response.data || [];
        })
        .catch(() => {
          this.newsRecords = [];
        })
        .finally(() => {
          this.newsLoading = false;
        });
    },
    formatCalendarDate(value) {
      return value ? DateTime.fromISO(value).toFormat('dd.MM.yyyy - HH:mm') : '-';
    },
    formatSqlDate(value) {
      return value ? DateTime.fromSQL(value).toFormat('dd.MM.yyyy') : '-';
    },
  },
};
</script>

<template>
  <Layout>
    <b-row class="mb-4">
      <b-col>
        <HeaderComponent
          :title="$t('titles.calendar')"
          :button-text="$t('actions.open')"
          @button-pressed="$router.push({ name: 'calendar.index' })">
        </HeaderComponent>
        <b-table
          class="mb-0"
          :items="calendarEntries"
          :fields="calendarFields"
          :busy="calendarLoading"
          responsive
          striped
          stacked="md"
          show-empty
          :empty-text="$t('messages.no_items')"
        >
          <template v-slot:cell(vip)="row">{{ row.item.vip || '-' }}</template>
          <template v-slot:cell(location)="row">{{ row.item.location || '-' }}</template>
        </b-table>
      </b-col>
    </b-row>

    <b-row class="mb-4">
      <b-col>
        <section class="dashboard-panel h-100" aria-labelledby="reports-heading">
          <HeaderComponent
            :title="$t('titles.reports')"
            :button-text="$t('actions.open')"
            @button-pressed="$router.push({ name: 'reports.index' })">
          </HeaderComponent>
          <b-table
            class="mb-0"
            :items="reports"
            :fields="reportFields"
            :busy="reportsLoading"
            responsive
            striped
            stacked="md"
            show-empty
            :empty-text="$t('messages.no_items')"
          >
            <template v-slot:cell(id)="row">
              <router-link :to="{ name: 'reports.edit', params: { id: row.item.id } }">#{{ row.item.id }}</router-link>
            </template>
            <template v-slot:cell(vip)="row">
              {{ row.item.vip ? row.item.vip.name : row.item.vip_description || '-' }}
            </template>
            <template v-slot:cell(completed)="row">
              <b-badge v-if="row.item.completed" variant="success">Completed</b-badge>
              <b-badge v-else variant="danger">Draft</b-badge>
            </template>
            <template v-slot:cell(meeting_point)="row">{{ row.item.meeting_point || '-' }}</template>
          </b-table>
        </section>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <section class="dashboard-panel h-100" aria-labelledby="news-heading">
          <HeaderComponent
            :title="$t('titles.news')"
            :button-text="$t('actions.open')"
            @button-pressed="$router.push({ name: 'news.index' })">
          </HeaderComponent>
          <b-table
            class="mb-0"
            :items="newsRecords"
            :fields="newsFields"
            :busy="newsLoading"
            responsive
            striped
            stacked="md"
            show-empty
            :empty-text="$t('messages.no_items')"
          >
            <template v-slot:cell(title)="row">
              <router-link :to="{ name: 'news.show', params: { id: row.item.id } }">{{ row.item.title }}</router-link>
            </template>
            <template v-slot:cell(attachment)="row">
              <FileDownloadLink v-if="row.item.attachment" :file="row.item.attachment" :removable="false" />
              <template v-else>-</template>
            </template>
          </b-table>
        </section>
      </b-col>
    </b-row>
  </Layout>
</template>